import { App, Card } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"

import { useDateFormat } from "../../../../../../hooks/use-date-format.ts"
import { useNotifications } from "../../../../../../hooks/use-notifications.ts"
import { nextPageUrl } from "../../../../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../../../../store.ts"
import { DATE_FORMAT } from "../../../../../../utils/date/date.ts"
import { extractParamsFromNextUrl } from "../../../../../../utils/form/extract-params-from-next-url.ts"
import { budgetFormat } from "../../../../../../utils/number/budget-format.ts"
import { serializeData } from "../../../../../../utils/utils"
import { canEditProject } from "../../../../helpers/project-edit-visibility.ts"
import { useProjectOutletContext } from "../../../../hooks/use-project-outlet-context.tsx"
import { type MilestoneDependencyWriteData } from "../../../../api/project-milestone-dependencies.services.ts"
import {
  calculateEffectiveSchedule,
  diffEffectiveSchedule,
  type EffectiveScheduleEntry
} from "../../../utils/milestone-effective-schedule.ts"
import {
  fetchFinancialAlerts,
  fetchFinancialSnapshot,
  fetchFinancialTransactionItemsFilterOptions
} from "../../../../redux/project-finance/project-finance.thunks.ts"
import {
  createMilestoneDependency,
  deleteMilestoneDependency,
  fetchProjectMilestoneDependencies,
  patchMilestoneDependency
} from "../../../../redux/project-milestones/project-milestone-dependencies.thunks.ts"
import {
  bulkDeleteProjectMilestones,
  bulkPatchProjectMilestones,
  createProjectMilestone,
  deleteProjectMilestone,
  fetchProjectMilestoneById,
  fetchProjectMilestones,
  fetchProjectMilestonesFilterOptions,
  patchProjectMilestone,
  reorderProjectMilestones
} from "../../../../redux/project-milestones/project-milestones.thunks.ts"
import { projectSetProp } from "../../../../redux/project.slice.ts"
import {
  addProjectTypeToProject,
  recordProjectTypeContractDecision,
  removeProjectTypeToProject,
  updateProjectTypeToProject,
  reorderProjectTypes
} from "../../../../redux/project.thunks.ts"
import type { ProjectMilestoneFilters } from "../../../../redux/project.types.ts"
import type { MilestoneDependency } from "../../../../types/project-milestone-dependency.types.ts"
import type {
  ProjectMilestone,
  ProjectMilestoneFinanceItem
} from "../../../../types/project-phases.types.tsx"
import {
  type ProjectTypeDetail,
  type ProjectTypeFormData
} from "../../../../types/project.types.ts"
import { type ContractDecision } from "../../../../api/services.types.ts"
import {
  deleteNeedsContractPrompt,
  projectTypeContractPrompt
} from "../../../../helpers/project-type-contract-prompt.ts"
import { PROJECT_ENTITY_CATEGORIES } from "../../../constants/project-entity-category.ts"
import {
  FINANCE_ITEM_TYPES,
  type FinanceItemTypes
} from "../../../constants/finance-item-constants.ts"
import { ContractDecisionModal } from "./ContractDecisionModal/ContractDecisionModal.tsx"
import { DeleteProjectTypeModal } from "./ContractDecisionModal/DeleteProjectTypeModal.tsx"
import { MilestoneDependenciesModal } from "./ProjectViewMilestones/MilestoneDependenciesModal.tsx"
import { ProjectMilestoneModal } from "./ProjectViewMilestones/ProjectMilestoneModal.tsx"
import { ProjectViewMilestonesGantt } from "./ProjectViewMilestones/ProjectViewMilestonesGantt.tsx"
import { ProjectViewMilestonesHeader } from "./ProjectViewMilestones/ProjectViewMilestonesHeader/ProjectViewMilestonesHeader.tsx"
import { type MilestoneBulkActionParams } from "./ProjectViewMilestones/MilestonesBulkActionBar.tsx"
import { ProjectViewMilestonesList } from "./ProjectViewMilestones/ProjectViewMilestonesList.tsx"
import {
  confirmRepeatedType,
  instanceKeyOf,
  isRepeatedTypeRefusal,
  newestInstanceOf,
  typeLabel
} from "../../../../helpers/repeated-project-type.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"

type MilestonesView = "table" | "gantt"
type EffectiveSchedule = Map<string, EffectiveScheduleEntry>
type ScheduleImpact = ReturnType<typeof diffEffectiveSchedule>

const shiftedOnly = (impact: ScheduleImpact, after: EffectiveSchedule) =>
  impact.filter((entry) => !after.get(entry.id)?.isGenerated)

// A per-browser UI preference, not project data — deliberately not project-
// scoped and not part of any redux slice, so it survives switching between
// project-type tabs (which never touches it) and between projects entirely
// (the "remember how I prefer to view this" behavior the user asked for).
const MILESTONES_VIEW_STORAGE_KEY = "ezbuild:milestones-view"

const readStoredMilestonesView = (): MilestonesView => {
  try {
    const stored = localStorage.getItem(MILESTONES_VIEW_STORAGE_KEY)
    return stored === "gantt" ? "gantt" : "table"
  } catch {
    return "table"
  }
}

const writeStoredMilestonesView = (view: MilestonesView) => {
  try {
    localStorage.setItem(MILESTONES_VIEW_STORAGE_KEY, view)
  } catch {
    // Best-effort only — a private window or blocked storage just means the
    // preference won't be remembered next time, not a broken feature.
  }
}

export const ProjectViewMilestonesSection = ({
  userId,
  projectPermissions,
  pages,
  data,
  fetching,
  filters,
  filterOptions,
  dependencies,
  createMilestone,
  updateMilestone,
  removeMilestone,
  reorderMilestones,
  bulkPatchMilestones,
  bulkDeleteMilestones,
  getFilterOptions,
  getFinanceFilterOptions,
  getProjectMilestones,
  getProjectMilestoneById,
  getMilestoneDependencies,
  createDependency,
  updateDependency,
  removeDependency,
  addProjectType,
  // updateProjectType,
  removeProjectType,
  updateProjectType,
  recordContractDecision,
  reorderProjectTypes,
  setProp,
  refetchFinancialSnapshot,
  getFinancialAlerts
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const { normalizeDate, parseDate } = useDateFormat()
  const { modal, notification } = App.useApp()
  const notifications = useNotifications()
  const { project } = useProjectOutletContext()

  const id = project?.id
  const count = data?.count || 0
  const milestones = data?.results || []
  const showFinanceItems =
    !!projectPermissions?.planned_finances?.project_planned_finance_view

  const [editableRow, setEditableRow] = useState<string>("")
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [showAddMilestone, setShowAddMilestone] = useState(false)
  const [pendingDecision, setPendingDecision] =
    useState<ProjectTypeDetail | null>(null)
  const [pendingDeletion, setPendingDeletion] =
    useState<ProjectTypeDetail | null>(null)
  const [pendingReversal, setPendingReversal] = useState<{
    previous: ProjectTypeDetail
    next: ProjectTypeFormData
  } | null>(null)
  const [financeOptions, setFinanceOptions] = useState<
    Record<string, BaseOptionType[]>
  >({})
  const [manageDependenciesFor, setManageDependenciesFor] =
    useState<ProjectMilestone | null>(null)
  const [savingDependency, setSavingDependency] = useState(false)
  const [view, setViewState] = useState<MilestonesView>(readStoredMilestonesView)
  const setView = useCallback((next: MilestonesView) => {
    setViewState(next)
    writeStoredMilestonesView(next)
  }, [])

  const setFilters = useCallback(
    (data: Partial<ProjectMilestoneFilters>) => {
      setProp({
        key: "milestonesFilters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const handleRefetchFinancialSnapshot = useCallback(() => {
    refetchFinancialSnapshot(id)
    // A milestone edit changes the totals the alert rules are evaluated
    // against — without this, a resolved (or newly triggered) condition
    // stays stale until something else happens to refetch alerts.
    void getFinancialAlerts(id)
  }, [refetchFinancialSnapshot, getFinancialAlerts, id])

  const handleGetMilestones = useCallback(async () => {
    const serializeFilters = serializeData(
      filters || {}
    ) as ProjectMilestoneFilters

    return getProjectMilestones({
      isMore: false,
      params: {
        lead: id,
        ...serializeFilters
      }
    })
  }, [id, filters, getProjectMilestones])

  // The API derives some of the milestone's figures from its ledger lines and
  // recomputes them after the write response is serialised, so a write that can
  // move one leaves the row it returned a step behind. Returns the refetched
  // milestones so a caller that needs the just-recomputed figures (e.g. to
  // compare Committed against Planned right after a financial-item write)
  // does not have to read them off a stale render-time closure.
  const refreshMilestonesAndSnapshot = useCallback(async () => {
    const result = await handleGetMilestones()
    handleRefetchFinancialSnapshot()
    return result
  }, [handleGetMilestones, handleRefetchFinancialSnapshot])

  const handleGetMoreMilestones = useCallback(async () => {
    const url = nextPageUrl(data)
    if (!url) return

    const serializeFilters =
      extractParamsFromNextUrl<ProjectMilestoneFilters>(url)

    await getProjectMilestones({
      isMore: true,
      params: { lead: id, ...serializeFilters }
    })
  }, [data?.next, data?.retryNext, getProjectMilestones, id])

  const handleChangePage = useCallback(
    (page: number) => setFilters({ page }),
    [setFilters]
  )

  const handleChangeFilter = useCallback(
    (newData: Partial<ProjectMilestoneFilters>) =>
      setFilters({ ...newData, page: 1 }),
    [setFilters]
  )

  const handleGetFilterOptions = useCallback(async () => {
    try {
      // todo remove it later pls
      const args = { project_id: id }
      await getFilterOptions(args).unwrap() // milestone filter options

      if (showFinanceItems) {
        const res = await getFinanceFilterOptions(args).unwrap()
        setFinanceOptions(res)
      }
    } catch (e) {
      console.log("Error getting filter options", e)
    }
  }, [getFilterOptions, getFinanceFilterOptions, showFinanceItems, id])

  const handleCreateMilestone = useCallback(
    async (data: ProjectMilestone) => {
      try {
        await createMilestone({
          ...data,
          lead: id,
          order: count + 1,
          financial_items: [],
          project_type: filters?.project_type,
          project_type_assignment: filters?.project_type_assignment
        }).unwrap()
        await refreshMilestonesAndSnapshot()
        return { ok: true }
      } catch (e) {
        console.log("Create milestone error: ", e)
        return { ok: false }
      }
    },
    [
      refreshMilestonesAndSnapshot,
      createMilestone,
      id,
      count,
      filters?.project_type
    ]
  )

  // The single place any milestone date edit goes through — table inline
  // edit today, and the Gantt if it ever gains its own date controls — so
  // the dependency cascade below runs identically regardless of origin.
  const applyMilestoneUpdate = useCallback(
    async (data: Partial<ProjectMilestone> & { id: string }) => {
      await updateMilestone(data).unwrap()
    },
    [updateMilestone]
  )

  // A friendly, non-technical confirmation that the schedule actually
  // changed — shown after any dependency create/update/delete, or an Actual
  // End entry, that moved at least one milestone, so the user isn't left
  // guessing whether their change took effect anywhere beyond the one field
  // they touched.
  const notifyScheduleUpdated = useCallback(
    (
      impact: ReturnType<typeof diffEffectiveSchedule>,
      nameById: Map<string, string>,
      singleMessage: (name: string, date: string) => string,
      pluralMessage: (count: number) => string
    ) => {
      if (!impact.length) return
      const description =
        impact.length === 1
          ? singleMessage(
              nameById.get(impact[0].id) || "A milestone",
              impact[0].window.start.format("MMM D")
            )
          : pluralMessage(impact.length)
      notification.success({ message: "Schedule updated", description })
    },
    [notification]
  )

  const notifyGeneratedSchedule = useCallback(
    (generatedIds: string[], after: EffectiveSchedule, nameById: Map<string, string>) => {
      if (!generatedIds.length) return
      const entry = generatedIds.length === 1 ? after.get(generatedIds[0]) : undefined
      const description = entry
        ? t("milestoneDependencies.scheduleGenerated.single", {
            name: nameById.get(generatedIds[0]) || t("milestoneDependencies.fallbackMilestone"),
            start: entry.start.format("MMM D"),
            end: entry.end.format("MMM D"),
            source:
              (entry.shiftReason && nameById.get(entry.shiftReason.sourceMilestoneId)) ||
              t("milestoneDependencies.fallbackSource")
          })
        : t("milestoneDependencies.scheduleGenerated.many", { count: generatedIds.length })
      notification.success({
        message: t("milestoneDependencies.scheduleUpdated"),
        description
      })
    },
    [notification, t]
  )

  // Only the dependency's own target is written back. A downstream milestone
  // the same edge generated dates for stays display-only, like any other
  // cascade, so it can still retreat when the chain above it moves.
  const persistGeneratedDates = useCallback(
    async (targetId: string, after: EffectiveSchedule) => {
      const entry = after.get(targetId)
      if (!entry?.isGenerated) return []
      await updateMilestone({
        id: targetId,
        start_date: entry.start.format(DATE_FORMAT),
        end_date: entry.end.format(DATE_FORMAT),
        silent: true
      }).unwrap()
      return [targetId]
    },
    [updateMilestone]
  )

  const applyDependencyImpact = useCallback(
    async ({
      targetId,
      after,
      shiftedImpact,
      nameById,
      shiftedSingle,
      shiftedMany
    }: {
      targetId: string
      after: EffectiveSchedule
      shiftedImpact: ScheduleImpact
      nameById: Map<string, string>
      shiftedSingle: (name: string, date: string) => string
      shiftedMany: (count: number) => string
    }) => {
      let generatedIds: string[] = []
      try {
        generatedIds = await persistGeneratedDates(targetId, after)
      } catch (e) {
        console.log("Persist generated dates error: ", e)
      }
      await handleGetMilestones()
      notifyScheduleUpdated(shiftedImpact, nameById, shiftedSingle, shiftedMany)
      notifyGeneratedSchedule(generatedIds, after, nameById)
    },
    [persistGeneratedDates, handleGetMilestones, notifyScheduleUpdated, notifyGeneratedSchedule]
  )

  const handleUpdateMilestone = useCallback(
    async (data: ProjectMilestone) => {
      try {
        // Compare against the EFFECTIVE previous dates, not the raw stored
        // ones — the edit form now opens pre-filled with a shifted
        // milestone's calculated dates (to match what the Table's read row
        // just showed), so if the user saves without touching them, this
        // must read as "unchanged" rather than as a manual edit that would
        // otherwise get persisted as the new baseline, permanently
        // overwriting the milestone's real stored dates with a snapshot of
        // a dependency shift.
        const originalRaw = milestones.find((m) => m.id === editableRow)
        const before = calculateEffectiveSchedule({ milestones, dependencies, parseDate })
        const previousEntry = before.get(editableRow)
        const previousStart = previousEntry?.start ?? null
        const previousEnd = previousEntry?.end ?? null
        const nextStart = parseDate(data.start_date)
        const nextEnd = parseDate(data.end_date)

        // Compared per field, and presence-first, so clearing a date reads as
        // a change rather than as "unchanged" — the same shape `actualEndChanged`
        // below already uses. Requiring both dates to be present treated a
        // cleared Planned End as no edit at all and dropped it from the write.
        const startChanged =
          !!previousStart !== !!nextStart ||
          (!!previousStart && !!nextStart && !nextStart.isSame(previousStart, "day"))
        const endChanged =
          !!previousEnd !== !!nextEnd ||
          (!!previousEnd && !!nextEnd && !nextEnd.isSame(previousEnd, "day"))

        const datesChanged = startChanged || endChanged

        // Actual End has no "effective" concept of its own — it's a raw fact
        // — so it's compared against its own previously stored value, not
        // anything derived.
        const previousActualEnd = originalRaw ? parseDate(originalRaw.actual_end_date) : null
        const nextActualEnd = parseDate(data.actual_end_date)
        const actualEndChanged = !!previousActualEnd !== !!nextActualEnd ||
          (!!previousActualEnd && !!nextActualEnd && !previousActualEnd.isSame(nextActualEnd, "day"))

        let cascade: ReturnType<typeof diffEffectiveSchedule> = []
        let direction: "later" | "earlier" = "later"
        let daysMoved = 0
        // Which of the two kinds of edit is driving the cascade, so the
        // confirmation and the post-save toast can each use wording that
        // actually describes what happened — "completed N days late" reads
        // very differently from "moved N days later".
        const isActualEndChange = actualEndChanged && !datesChanged

        if ((datesChanged || actualEndChanged) && nextStart && nextEnd) {
          // Same engine the Gantt renders from: recompute the whole schedule
          // with this one milestone's dates (and/or actual end) swapped in,
          // and diff it against today's schedule to see exactly what else
          // needs to move. Only this one milestone's own stored dates are
          // ever written below — a dependent's stored dates are never
          // overwritten with a calculated cascade value, so a later move
          // (or a late actual finish) that pushes a dependent out can just
          // as correctly retreat it again if the source is later pulled
          // back earlier (or its actual finish is corrected earlier).
          // Persisting a cascaded value would bake it in as if it were the
          // dependent's own manually chosen date, permanently blocking that
          // retreat.
          const after = calculateEffectiveSchedule({
            milestones,
            dependencies,
            parseDate,
            overrides: new Map([
              [
                editableRow,
                {
                  ...(datesChanged ? { start: nextStart, end: nextEnd } : {}),
                  ...(actualEndChanged ? { actualEnd: nextActualEnd } : {})
                }
              ]
            ])
          })
          cascade = diffEffectiveSchedule(before, after, {
            excludeIds: new Set([editableRow])
          })

          if (datesChanged) {
            const startDelta =
              previousStart && !nextStart.isSame(previousStart, "day")
                ? nextStart.diff(previousStart, "day")
                : previousEnd
                  ? nextEnd.diff(previousEnd, "day")
                  : 0
            direction = startDelta < 0 ? "earlier" : "later"
            daysMoved = Math.abs(startDelta)
          } else if (actualEndChanged && nextActualEnd && previousEnd) {
            // "Later/earlier than planned" always compares the actual finish
            // against the PLANNED end — not against whatever actual end (if
            // any) was previously recorded.
            const endDelta = nextActualEnd.diff(previousEnd, "day")
            direction = endDelta < 0 ? "earlier" : "later"
            daysMoved = Math.abs(endDelta)
          }
        }

        const applyAll = async () => {
          // The edit form opens pre-filled with a shifted milestone's
          // CALCULATED dates (so it matches what the Table's read row just
          // showed) — if the user saves without touching them, `data` still
          // carries those calculated values. Sending them through anyway
          // would silently overwrite the milestone's real stored dates with
          // a snapshot of the current dependency shift the moment ANY other
          // field is edited and saved, permanently erasing the distinction
          // this whole engine depends on. Only include start/end in the
          // write when the user actually changed them.
          const { start_date: _start_date, end_date: _end_date, ...rest } = data
          const payload = datesChanged ? data : rest
          await applyMilestoneUpdate({ ...payload, id: editableRow })
          await refreshMilestonesAndSnapshot()
          setEditableRow("")
          // Only the Actual End flow gets the friendly toast here — a plain
          // planned-date edit already has its own explicit "Move & Update"
          // confirmation immediately before this runs, so a second message
          // announcing the same thing again would be redundant.
          if (isActualEndChange) {
            notifyScheduleUpdated(
              cascade,
              nameById,
              (name) => `${name} was rescheduled to start after ${data.name} was completed.`,
              (count) =>
                `${data.name} was completed ${daysMoved} day${daysMoved === 1 ? "" : "s"} ${direction} than planned. ${count} dependent milestones were rescheduled.`
            )
          }
        }

        const nameById = new Map(milestones.map((m) => [m.id, m.name]))

        if (!cascade.length) {
          await applyAll()
          return
        }

        const count = cascade.length
        const daysLabel = daysMoved ? `${daysMoved} day${daysMoved === 1 ? "" : "s"} ` : ""

        modal.confirm({
          title: isActualEndChange ? "Schedule impact" : "Milestone moved",
          content: (
            <>
              <p>
                {isActualEndChange ? (
                  <>
                    Completing {data.name} {daysLabel}
                    {direction} than planned will also move {count} dependent
                    milestone{count === 1 ? "" : "s"}.
                  </>
                ) : (
                  <>
                    Moving {data.name} {daysLabel}
                    {direction} will also move {count} dependent milestone
                    {count === 1 ? "" : "s"} {direction}
                    {direction === "later" ? " to keep the project schedule aligned." : "."}
                  </>
                )}
              </p>
              <ul className="list-disc pl-4">
                {cascade.map(({ id, window, previousWindow }) => (
                  <li key={id}>
                    {nameById.get(id) || "Milestone"}:{" "}
                    {previousWindow && (
                      <>
                        {previousWindow.start.format("MMM D")} –{" "}
                        {previousWindow.end.format("MMM D")} →{" "}
                      </>
                    )}
                    {window.start.format("MMM D")} – {window.end.format("MMM D")}
                  </li>
                ))}
              </ul>
            </>
          ),
          okText: isActualEndChange ? "Update schedule" : "Move & Update",
          cancelText: "Cancel",
          onOk: applyAll
        })
      } catch (e) {
        console.log("Update milestone error: ", e)
      }
    },
    [
      refreshMilestonesAndSnapshot,
      editableRow,
      applyMilestoneUpdate,
      milestones,
      dependencies,
      parseDate,
      modal,
      notifyScheduleUpdated
    ]
  )

  const handleDeleteMilestone = useCallback(
    async (id: string) => {
      modal.confirm({
        title: "Delete Milestone",
        content: (
          <>
            <p>Are you sure you want to delete this milestone?</p>
            <p>
              All related financial items and tasks will be deleted as well.
            </p>
          </>
        ),
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await removeMilestone(id).unwrap()

            handleRefetchFinancialSnapshot()
          } catch (e) {
            console.log("Delete milestone error: ", e)
          }
        }
      })
    },
    [
      handleRefetchFinancialSnapshot,
      removeMilestone,
      handleGetMilestones,
      filters?.page,
      milestones?.length,
      setFilters
    ]
  )

  // Milestones have no bulk endpoint: each action fans the existing per-row
  // call out and comes back with a per-id outcome. The section owns the list
  // refresh (the milestone figures are derived server-side) and drops the
  // selection only once nothing in it was refused.
  const handleBulkAction = useCallback(
    async ({ action, ids, status }: MilestoneBulkActionParams) => {
      const result =
        action === "delete"
          ? await bulkDeleteMilestones({ ids }).unwrap()
          : await bulkPatchMilestones({ ids, status: status as number }).unwrap()

      await refreshMilestonesAndSnapshot()
      if (!result.failed.length) setSelectedIds([])
      return result
    },
    [bulkDeleteMilestones, bulkPatchMilestones, refreshMilestonesAndSnapshot]
  )

  // Both keys: a tab is its own working set, and a payload naming no instance
  // moves only the type.
  useEffect(() => {
    setSelectedIds([])
  }, [filters?.project_type, filters?.project_type_assignment])

  const onSelectRow = useCallback(
    (record: ProjectMilestone, action: string) => {
      switch (action) {
        case "edit_row":
          return setEditableRow(record?.id)
        case "update":
          return handleUpdateMilestone(record)
        case "cancel":
          return setEditableRow("")
        case "delete_row":
          return handleDeleteMilestone(record?.id)
        case "manage_dependencies":
          return setManageDependenciesFor(record)
      }
    },
    [setEditableRow, handleUpdateMilestone, handleDeleteMilestone]
  )

  const handleCreateDependency = useCallback(
    async (data: MilestoneDependencyWriteData) => {
      const before = calculateEffectiveSchedule({ milestones, dependencies, parseDate })

      if (!before.has(data.source_milestone) && !before.has(data.target_milestone)) {
        notifications.error({
          message: t("milestoneDependencies.datesRequired.title"),
          description: t("milestoneDependencies.datesRequired.description")
        })
        return false
      }

      // Preview what adding this relationship would do to the schedule
      // before it exists — the target milestone (and anything downstream of
      // it) may currently sit at dates the new dependency would forbid.
      const proposedDependency: MilestoneDependency = {
        ...data,
        id: "__pending__",
        type: data.type,
        type_display: "",
        created: "",
        updated: ""
      }
      const after = calculateEffectiveSchedule({
        milestones,
        dependencies: [...dependencies, proposedDependency],
        parseDate
      })
      const impact = diffEffectiveSchedule(before, after)
      const nameById = new Map(milestones.map((m) => [m.id, m.name]))
      const sourceName = nameById.get(data.source_milestone) || "the milestone it depends on"

      // A freshly generated window was never at a "real" date the user could
      // be surprised to see move, so only genuine shifts need confirming.
      const shiftedImpact = shiftedOnly(impact, after)

      const persist = async () => {
        setSavingDependency(true)
        try {
          await createDependency(data).unwrap()
          // The table's Dependencies column reads `dependency_count` off the
          // milestone record, so the refetch inside is not optional here.
          await applyDependencyImpact({
            targetId: data.target_milestone,
            after,
            shiftedImpact,
            nameById,
            shiftedSingle: (name, date) => `${name} was moved to ${date} to follow ${sourceName}.`,
            shiftedMany: (count) => `Adding this dependency shifted ${count} dependent milestones.`
          })
          return true
        } catch (e) {
          console.log("Create dependency error: ", e)
          return false
        } finally {
          setSavingDependency(false)
        }
      }

      if (!shiftedImpact.length) return persist()

      const count = shiftedImpact.length

      return new Promise<boolean>((resolve) => {
        modal.confirm({
          title: "Schedule impact",
          content: (
            <>
              <p>
                Adding this dependency will also shift {count} milestone
                {count === 1 ? "" : "s"} to keep the project schedule aligned.
              </p>
              <ul className="list-disc pl-4">
                {shiftedImpact.map(({ id, window, previousWindow }) => (
                  <li key={id}>
                    {nameById.get(id) || "Milestone"}:{" "}
                    {previousWindow && (
                      <>
                        {previousWindow.start.format("MMM D")} –{" "}
                        {previousWindow.end.format("MMM D")} →{" "}
                      </>
                    )}
                    {window.start.format("MMM D")} – {window.end.format("MMM D")}
                  </li>
                ))}
              </ul>
            </>
          ),
          okText: "Add dependency & update schedule",
          cancelText: "Cancel",
          onOk: async () => resolve(await persist()),
          onCancel: () => resolve(false)
        })
      })
    },
    [
      createDependency,
      applyDependencyImpact,
      milestones,
      dependencies,
      parseDate,
      modal,
      notifications,
      t
    ]
  )

  const handleUpdateDependency = useCallback(
    async (id: string, data: Partial<MilestoneDependencyWriteData>) => {
      // Same preview-before-persist shape as create: a lag or relationship
      // type change is just as much a scheduling constraint change as a
      // brand new dependency, so it goes through the same engine.
      const before = calculateEffectiveSchedule({ milestones, dependencies, parseDate })
      const after = calculateEffectiveSchedule({
        milestones,
        dependencies: dependencies.map((d) => (d.id === id ? { ...d, ...data } : d)),
        parseDate
      })
      const impact = diffEffectiveSchedule(before, after)
      const nameById = new Map(milestones.map((m) => [m.id, m.name]))
      const shiftedImpact = shiftedOnly(impact, after)
      const targetId =
        data.target_milestone ?? dependencies.find((d) => d.id === id)?.target_milestone

      setSavingDependency(true)
      try {
        await updateDependency({ ...data, id }).unwrap()
        if (targetId) {
          await applyDependencyImpact({
            targetId,
            after,
            shiftedImpact,
            nameById,
            shiftedSingle: (name, date) => `${name} was moved to ${date}.`,
            shiftedMany: (count) => `Updating this dependency shifted ${count} dependent milestones.`
          })
        }
        return true
      } catch (e) {
        console.log("Update dependency error: ", e)
        return false
      } finally {
        setSavingDependency(false)
      }
    },
    [updateDependency, dependencies, milestones, parseDate, applyDependencyImpact]
  )

  const handleDeleteDependency = useCallback(
    async (id: string) => {
      // Preview the effect of the constraint going away before it does —
      // a milestone that was only at its current dates because of this
      // dependency should be free to recalculate once it's gone.
      const before = calculateEffectiveSchedule({ milestones, dependencies, parseDate })
      const after = calculateEffectiveSchedule({
        milestones,
        dependencies: dependencies.filter((d) => d.id !== id),
        parseDate
      })
      const impact = diffEffectiveSchedule(before, after)
      const nameById = new Map(milestones.map((m) => [m.id, m.name]))

      try {
        await removeDependency(id).unwrap()
        // Same staleness concern as create: the row's own dependency_count
        // only updates on a fresh milestones fetch.
        await handleGetMilestones()
        notifyScheduleUpdated(
          impact,
          nameById,
          (name, date) => `${name} was recalculated to ${date} after removing this dependency.`,
          (count) => `Removing this dependency recalculated ${count} milestones.`
        )
        return true
      } catch (e) {
        console.log("Delete dependency error: ", e)
        return false
      }
    },
    [removeDependency, handleGetMilestones, milestones, dependencies, parseDate, notifyScheduleUpdated]
  )

  // A financial item is a line on the ledger, not the plan — adding or
  // editing one can push Committed past Planned without anyone having
  // touched the milestone's own Planned field. Rather than silently letting
  // the two disagree (or auto-overwriting Planned), offer the user the
  // choice, the same way any other write-vs-plan mismatch in this app asks
  // rather than assumes.
  //
  // Payable only. A milestone's Payable has no auto-generated line — every
  // payable line is manual — so syncing Planned up to Committed converges.
  // Receivable is structurally different: `sync_milestone_receivable_on_save`
  // (ezbuild_back/apps/project_management/signals.py) keeps an auto-generated
  // receivable line mirrored to `planned_receivable`, so that line is always
  // part of `committed_receivable`. The instant any *manual* receivable line
  // also exists, `committed_receivable = planned_receivable + manual_total`
  // holds by construction — raising Planned to match Committed also raises
  // the auto-line (since it re-mirrors the new Planned), so Committed moves
  // right back out of reach by the same `manual_total` on every attempt. No
  // target value converges; offering a "fix" here can only loop. Receivable's
  // disagreement stays a red flag only (MilestoneFinancialCell), the same way
  // a Payable's disagreement was already surfaced with no auto-line and no
  // sync offered, before this feature existed.
  const offerPlannedSync = useCallback(
    (
      updatedMilestone: ProjectMilestone | undefined,
      itemType: FinanceItemTypes | undefined
    ) => {
      if (!updatedMilestone || itemType !== FINANCE_ITEM_TYPES.PAYABLE) return

      const label = t("milestonePlannedSync.payableLabel")
      const committed = Number(updatedMilestone.committed_payable) || 0
      const planned = Number(updatedMilestone.planned_payable) || 0

      if (committed <= planned) return

      modal.confirm({
        title: t("milestonePlannedSync.title"),
        content: t("milestonePlannedSync.content", {
          label,
          planned: budgetFormat(planned),
          committed: budgetFormat(committed)
        }),
        okText: t("milestonePlannedSync.ok"),
        cancelText: t("milestonePlannedSync.cancel"),
        onOk: async () => {
          await updateMilestone({
            id: updatedMilestone.id,
            planned_payable: committed,
            silent: true
          }).unwrap()
          await refreshMilestonesAndSnapshot()
          notification.success({
            message: t("milestonePlannedSync.success", {
              label,
              value: budgetFormat(committed)
            })
          })
        }
      })
    },
    [modal, notification, updateMilestone, refreshMilestonesAndSnapshot, t]
  )

  const handleDeleteMilestoneFinanceItem = useCallback(
    async (itemId: string, milestone: ProjectMilestone) => {
      try {
        await updateMilestone({
          id: milestone.id,
          financial_items: milestone.financial_items?.filter(
            (item) => item.id !== itemId
          )
        }).unwrap()

        await refreshMilestonesAndSnapshot()
      } catch (e) {
        console.error("Delete finance item error:", e)
      }
    },
    [refreshMilestonesAndSnapshot, updateMilestone]
  )

  const handleUpdateMilestoneFinanceItem = useCallback(
    async (
      itemId: string,
      data: Partial<ProjectMilestoneFinanceItem>,
      milestone: ProjectMilestone
    ) => {
      try {
        const existingItem = milestone.financial_items?.find(
          (item) => item.id === itemId
        )

        await updateMilestone({
          id: milestone.id,
          financial_items: milestone.financial_items?.map((item) =>
            item.id === itemId
              ? { ...item, ...data }
              : {
                  ...item,
                  date: normalizeDate(item?.date),
                  due_date: normalizeDate(item?.due_date)
                }
          )
        }).unwrap()
        const [freshMilestone] = await Promise.all([
          getProjectMilestoneById(milestone.id).unwrap(),
          refreshMilestonesAndSnapshot()
        ])
        offerPlannedSync(freshMilestone, data.item_type ?? existingItem?.item_type)
      } catch (e) {
        console.error("Update finance item error:", e)
      }
    },
    [
      refreshMilestonesAndSnapshot,
      updateMilestone,
      getProjectMilestoneById,
      offerPlannedSync
    ]
  )

  const handleAddMilestoneFinanceItem = useCallback(
    async (
      data: Partial<ProjectMilestoneFinanceItem>,
      milestone: ProjectMilestone
    ) => {
      try {
        await updateMilestone({
          id: milestone.id,
          financial_items: [
            ...(milestone.financial_items?.map((el) => ({
              ...el,
              date: normalizeDate(el?.date),
              due_date: normalizeDate(el?.due_date)
            })) || []),
            { ...data, project_type: filters?.project_type }
          ] as ProjectMilestone["financial_items"]
        }).unwrap()
        const [freshMilestone] = await Promise.all([
          getProjectMilestoneById(milestone.id).unwrap(),
          refreshMilestonesAndSnapshot()
        ])
        offerPlannedSync(freshMilestone, data.item_type)
        return true
      } catch (e) {
        console.error("Add finance item error:", e)
        return false
      }
    },
    [
      refreshMilestonesAndSnapshot,
      updateMilestone,
      getProjectMilestoneById,
      offerPlannedSync,
      filters?.project_type,
      count
    ]
  )

  const handleAddProjectType = useCallback(
    async (data: ProjectTypeFormData) => {
      let details: ProjectTypeDetail[] | undefined

      try {
        details = await addProjectType({ projectId: id, data }).unwrap()
      } catch (error) {
        if (!isRepeatedTypeRefusal(error)) throw error
        // Declining is not a finished add: closing drops what was just typed.
        if (!(await confirmRepeatedType(modal, t))) return false

        details = await addProjectType({
          projectId: id,
          data: { ...data, allow_duplicate: true }
        }).unwrap()
      }

      if (data.category !== PROJECT_ENTITY_CATEGORIES.CHANGE_REQUEST) return

      // Every instance of a type shares its `id`, so a plain find gets the first.
      const created = newestInstanceOf(details, data.project_type_id)
      if (created) setPendingDecision(created)
    },
    [addProjectType, modal, t, id]
  )

  const handleUpdateProjectType = useCallback(
    async (data: ProjectTypeFormData, previous: ProjectTypeDetail) => {
      const prompt = projectTypeContractPrompt(previous, data.category)

      if (prompt === "reverse-contract") {
        setPendingReversal({ previous, next: data })
        return
      }

      const details = await updateProjectType({ projectId: id, data }).unwrap()
      await refreshMilestonesAndSnapshot()

      if (prompt !== "contract-decision") return

      const updated = details?.find((detail: ProjectTypeDetail) =>
        data.assignment_id
          ? detail.assignment_id === data.assignment_id
          : detail.id === data.project_type_id
      )
      if (updated) setPendingDecision(updated)
    },
    [updateProjectType, refreshMilestonesAndSnapshot, id]
  )

  const handleContractDecision = useCallback(
    async (decision: ContractDecision) => {
      if (!pendingDecision) return
      await recordContractDecision({
        projectId: id,
        data: {
          project_type_id: pendingDecision.id,
          assignment_id: pendingDecision.assignment_id,
          decision
        }
      }).unwrap()
      setPendingDecision(null)
      await refreshMilestonesAndSnapshot()
    },
    [recordContractDecision, refreshMilestonesAndSnapshot, pendingDecision, id]
  )

  const handleReorderMilestones = useCallback(
    (orderedIds: string[]) => {
      void reorderMilestones(orderedIds.map((id, order) => ({ id, order })))
    },
    [reorderMilestones]
  )

  const handleReorderProjectTypes = useCallback(
    async (orderedKeys: string[]) => {
      const details = project?.project_type_details ?? []
      const items = orderedKeys
        .map((key, order) => {
          const detail = details.find(
            (item) => instanceKeyOf(item) === key
          )
          return detail
            ? {
                project_type_id: detail.id,
                assignment_id: detail.assignment_id,
                order
              }
            : null
        })
        .filter((item) => item !== null)

      await reorderProjectTypes({ projectId: id, items }).unwrap()
    },
    [reorderProjectTypes, project?.project_type_details, id]
  )

  // By type when the payload names no instance: a backend deployed before this
  // one sends no assignment id, and a bare assignment id matches no tab.
  const activeTabKey = useMemo(() => {
    const details = project?.project_type_details ?? []
    const open = details.find((detail) =>
      filters?.project_type_assignment
        ? detail.assignment_id === filters.project_type_assignment
        : detail.id === filters?.project_type
    )
    return open ? instanceKeyOf(open) : ""
  }, [
    project?.project_type_details,
    filters?.project_type_assignment,
    filters?.project_type
  ])

  const handleDeleteProjectType = useCallback(
    async (detail: ProjectTypeDetail) => {
      if (detail && deleteNeedsContractPrompt(detail)) {
        setPendingDeletion(detail)
        return
      }

      modal.confirm({
        title: t("projectTypeRemove.title"),
        content: (
          <>
            <p>{t("projectTypeRemove.lede", { count: 1 })}</p>
            <p>{t("projectTypeRemove.consequence")}</p>
          </>
        ),
        okText: t("projectTypeRemove.confirm"),
        okType: "danger",
        cancelText: t("projectTypeRemove.cancel"),
        onOk: async () => {
          await removeProjectType({
            projectId: id,
            project_type_id: detail.id,
            assignment_id: detail.assignment_id
          }).unwrap()
          await refreshMilestonesAndSnapshot()
        }
      })
    },
    [modal, t, removeProjectType, refreshMilestonesAndSnapshot, id]
  )

  const handleConfirmedDeletion = useCallback(
    async (reverse_contract: boolean) => {
      if (!pendingDeletion) return
      await removeProjectType({
        projectId: id,
        project_type_id: pendingDeletion.id,
        assignment_id: pendingDeletion.assignment_id,
        reverse_contract
      }).unwrap()
      setPendingDeletion(null)
      await refreshMilestonesAndSnapshot()
    },
    [removeProjectType, refreshMilestonesAndSnapshot, pendingDeletion, id]
  )

  const handleConfirmedReversal = useCallback(
    async (reverse_contract: boolean) => {
      if (!pendingReversal) return
      const { next } = pendingReversal

      await updateProjectType({
        projectId: id,
        data: { ...next, reverse_contract }
      }).unwrap()
      setPendingReversal(null)
      await refreshMilestonesAndSnapshot()
    },
    [updateProjectType, refreshMilestonesAndSnapshot, pendingReversal, id]
  )

  useEffect(() => {
    void handleGetMilestones()
  }, [id, handleGetMilestones])

  useEffect(() => {
    if (id) void getMilestoneDependencies(id)
  }, [id, getMilestoneDependencies])

  useEffect(() => {
    void handleGetFilterOptions()
  }, [handleGetFilterOptions])

  // `overflow-clip`, not `auto` or `hidden`: both of those make this card a
  // scroll container, and the add control's `position: sticky` then resolves
  // against a box that never scrolls — so it rode down with the table instead
  // of holding above the fold. `clip` still clips the flush table to the
  // card's radius but is not a scrollport, so the action sticks to
  // `FixedContainer`, which is what actually scrolls here.
  return (
    <Card
      className="overflow-clip"
      classNames={{ body: "p-0", header: "p-2" }}
      title={
        <ProjectViewMilestonesHeader
          project={project}
          isLocked={!!editableRow}
          activeTabKey={activeTabKey}
          //
          projectEditAllowed={canEditProject(projectPermissions, project)}
          editAllowed={projectPermissions?.milestones?.project_milestone_edit}
          //
          view={view}
          onViewChange={setView}
          //
          onFilterChange={handleChangeFilter}
          onAddProjectType={handleAddProjectType}
          onDeleteProjectType={handleDeleteProjectType}
          onReorderProjectTypes={handleReorderProjectTypes}
          onUpdateProjectType={handleUpdateProjectType}
        />
      }
    >
      {pendingDecision && (
        <ContractDecisionModal
          open
          projectTypeName={typeLabel(pendingDecision)}
          estimatedReceivable={Number(pendingDecision.estimated_receivable ?? 0)}
          estimatedPayable={Number(pendingDecision.estimated_payable ?? 0)}
          canUpdateContract={
            !!projectPermissions?.finances?.project_finance_edit
          }
          onDecide={handleContractDecision}
        />
      )}

      {pendingDeletion && (
        <DeleteProjectTypeModal
          open
          isIncludedInContract
          onCancel={() => setPendingDeletion(null)}
          onDelete={handleConfirmedDeletion}
        />
      )}

      {pendingReversal && (
        <DeleteProjectTypeModal
          open
          isIncludedInContract
          onCancel={() => setPendingReversal(null)}
          onDelete={handleConfirmedReversal}
        />
      )}

      {showAddMilestone && (
        <ProjectMilestoneModal
          open
          options={filterOptions}
          onClose={() => setShowAddMilestone(false)}
          onSubmit={handleCreateMilestone}
        />
      )}

      {view === "table" ? (
        <ProjectViewMilestonesList
          showFinanceItems={showFinanceItems}
          userId={userId}
          projectPermissions={projectPermissions}
          projectId={id}
          milestones={milestones}
          dependencies={dependencies}
          contractValue={project?.sales_price ?? null}
          contractExpenses={project?.planned_payable ?? null}
          options={filterOptions}
          financeOptions={financeOptions}
          next={data?.next}
          retryNext={data?.retryNext}
          count={count}
          editableRow={editableRow}
          fetching={fetching}
          createAllowed={projectPermissions?.milestones?.project_milestone_create}
          createDisabled={!filters?.project_type || !!editableRow}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          onBulkAction={handleBulkAction}
          onAddMilestone={() => setShowAddMilestone(true)}
          onLoadMore={handleGetMoreMilestones}
          onSelectRow={onSelectRow}
          onPageChange={handleChangePage}
          onDeleteMilestoneFinanceItem={handleDeleteMilestoneFinanceItem}
          onUpdateMilestoneFinanceItem={handleUpdateMilestoneFinanceItem}
          onAddMilestoneFinanceItem={handleAddMilestoneFinanceItem}
          onReorderMilestones={handleReorderMilestones}
        />
      ) : (
        <div className="p-3">
          <ProjectViewMilestonesGantt
            milestones={milestones}
            dependencies={dependencies}
            projectName={project?.lead_name}
          />
        </div>
      )}

      {manageDependenciesFor && (
        <MilestoneDependenciesModal
          milestone={manageDependenciesFor}
          milestones={milestones}
          dependencies={dependencies}
          saving={savingDependency}
          onClose={() => setManageDependenciesFor(null)}
          onCreate={handleCreateDependency}
          onUpdate={handleUpdateDependency}
          onDelete={handleDeleteDependency}
        />
      )}
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  userId: state.auth.userInfo.id,
  projectPermissions: selectProjectWritePermissions(state),
  pages: state.auth.permissions.pages,
  filters: state.project.milestonesFilters,
  filterOptions: state.project.milestonesFilterOptions,
  fetching: state.project.fetchingSection,
  data: state.project.milestones,
  dependencies: state.project.milestoneDependencies
})

const mapDispatchToProps = {
  createMilestone: createProjectMilestone,
  updateMilestone: patchProjectMilestone,
  removeMilestone: deleteProjectMilestone,
  reorderMilestones: reorderProjectMilestones,
  bulkPatchMilestones: bulkPatchProjectMilestones,
  bulkDeleteMilestones: bulkDeleteProjectMilestones,
  getProjectMilestones: fetchProjectMilestones,
  getProjectMilestoneById: fetchProjectMilestoneById,
  getFilterOptions: fetchProjectMilestonesFilterOptions,
  getFinanceFilterOptions: fetchFinancialTransactionItemsFilterOptions,
  getMilestoneDependencies: fetchProjectMilestoneDependencies,
  createDependency: createMilestoneDependency,
  updateDependency: patchMilestoneDependency,
  removeDependency: deleteMilestoneDependency,
  addProjectType: addProjectTypeToProject,
  updateProjectType: updateProjectTypeToProject,
  recordContractDecision: recordProjectTypeContractDecision,
  removeProjectType: removeProjectTypeToProject,
  reorderProjectTypes,
  setProp: projectSetProp,
  refetchFinancialSnapshot: fetchFinancialSnapshot,
  getFinancialAlerts: fetchFinancialAlerts
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewMilestonesSection)
